import { Controller } from '@hotwired/stimulus';
import Cleave from 'cleave.js';
require('cleave.js/dist/addons/cleave-phone.i18n');
import { getCountries, getCountryCallingCode } from 'libphonenumber-js';

export default class extends Controller {
	static values = { type: String, country: { type: String, default: 'FR' } };
	static targets = ['input', 'country'];

	connect() {
		if (this.typeValue === 'phone') this._phone();
		if (this.typeValue === 'number') this._number();
	}

	_phone() {
		const inputEl = this.hasInputTarget ? this.inputTarget : this.element.querySelector('input');
		if (!inputEl) return;

		const initialRegion = (this.countryValue || 'FR').toUpperCase();
		this._reinitCleave(inputEl, initialRegion);

		try {
			const initialCalling = getCountryCallingCode(initialRegion);
			if (!inputEl.value || inputEl.value.trim() === '') {
				this.cleave.setRawValue(`+${initialCalling}`);
			}
		} catch (_) {}

		if (this.hasCountryTarget && this.countryTarget) {
			try { this.countryTarget.value = initialRegion; } catch (_) {}
			this._enhanceCountrySelectLabels();
		}

		if (!this.callingCodeToCountry) {
			const map = {};
			const prefer = { '1': 'US', '7': 'RU' };
			try {
				for (const c of getCountries()) {
					const code = getCountryCallingCode(c);
					if (!map[code] || prefer[code] === c) map[code] = c;
				}
				this.callingCodeToCountry = map;
			} catch (_) {}
		}

		const detectFromPrefix = (value) => {
			if (!value) return null;
			const digits = value.replace(/\D/g, '');
			if (!digits) return null;
			const map = this.callingCodeToCountry || {};
			if (digits.length >= 3 && map[digits.slice(0,3)]) return map[digits.slice(0,3)];
			if (digits.length >= 2 && map[digits.slice(0,2)]) return map[digits.slice(0,2)];
			if (digits.length >= 1 && map[digits.slice(0,1)]) return map[digits.slice(0,1)];
			return null;
		};

		const onInput = (e) => {
			const region = detectFromPrefix(e.target.value);
			if (region && this.cleave) {
				const el = this.hasInputTarget ? this.inputTarget : this.element.querySelector('input');
				this._reinitCleave(el, region);
				if (this.hasCountryTarget && this.countryTarget) {
					try { this.countryTarget.value = region; } catch (_) {}
				}
			}
		};

		inputEl.addEventListener('input', onInput);
	}

	_enhanceCountrySelectLabels() {
		if (!(this.hasCountryTarget && this.countryTarget)) return;
		const select = this.countryTarget;
		let displayNames;
		try {
			const lang = document?.documentElement?.lang || 'fr';
			displayNames = new Intl.DisplayNames([lang], { type: 'region' });
		} catch (_) {}
		for (const option of Array.from(select.options)) {
			const cc = (option.value || '').toUpperCase();
			if (!cc) continue;
			let name = cc;
			try { name = displayNames?.of(cc) || cc; } catch (_) {}
			let calling = '';
			try { calling = getCountryCallingCode(cc); } catch (_) {}
			option.textContent = `${name} (+${calling})`;
		}
	}

	changeCountry(event) {
		const code = (event?.target?.value || this.countryValue || 'FR').toUpperCase();
		const inputEl = this.hasInputTarget ? this.inputTarget : this.element.querySelector('input');
		if (this.cleave && code && inputEl) {
			this._reinitCleave(inputEl, code);
			try {
				const calling = getCountryCallingCode(code);
				this.cleave.setRawValue(`+${calling}`);
			} catch (_) {}
		}
	}

	_reinitCleave(inputEl, region) {
		if (!inputEl) return;
		const currentValue = inputEl.value;
		if (this.cleave && this.cleave.destroy) this.cleave.destroy();
		this.cleave = new Cleave(inputEl, {
			phone: true,
			phoneRegionCode: region,
			prefix: '+',
			noImmediatePrefix: true,
		});
		try { this.cleave.setRawValue(currentValue); } catch (_) {}
	}

	_number() {
		this.cleave = new Cleave(this.element, {
			numeral: true,
			timePattern: ['h'],
		});
	}
}
